import { Plus, X } from 'lucide-react';
import React, { useState, useEffect, useRef, type MouseEvent } from 'react'
import MoreLabels from './MoreLabels'
import AddLabels, { type AdminTag } from './AddLabels';
import { labelStyle } from '@univdiam/ui/lib/tagColors';

type TagCellProps = {
  labels: string[];
  orderId: string;
  orderRef: string;
  customerId: string;
  newUpdate: boolean;
  adminTags?: AdminTag[];
  fetchTags?: any;
  onUpdateOrder?: (order_id: string, customer_id: string, updates: { labels?: string[]; status?: string; is_new_update?: boolean; due_date?: string | null; orderRef: string }) => void;
};

function TagsCell({ orderId, orderRef, customerId, labels, newUpdate, adminTags = [], fetchTags, onUpdateOrder }: TagCellProps) {

  const VisibleTags = labels.slice(0, 2)
  const HiddenTags = labels.slice(2)

  const [isHover, setIsHover] = useState(false)
  const [isHoverChip, setIsHoverChip] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [positionAbove, setPositionAbove] = useState(false)

  const cellRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClose = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.exceptOrderId !== orderId) {
        setIsActive(false);
      }
    };
    window.addEventListener('close-all-label-popovers', handleClose);
    return () => window.removeEventListener('close-all-label-popovers', handleClose);
  }, [orderId]);

  const handleMouseEnter = () => { setIsHover(true); }
  const handleMouseLeave = () => { setIsHover(false); }
  const handleToggleActive = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!isActive) {
      if (cellRef.current) {
        const rect = cellRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        // Popover height is around 260px-300px
        if (spaceBelow < 280) {
          setPositionAbove(true);
        } else {
          setPositionAbove(false);
        }
      }
      window.dispatchEvent(new CustomEvent('close-all-label-popovers', { detail: { exceptOrderId: orderId } }));
    }
    setIsActive((prev) => !prev)
  };

  return (

    <div
      ref={cellRef}
      className={`relative flex gap-2 items-center min-h-[32px] h-full transition-all duration-150 ${isActive ? 'z-30' : 'z-10'}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={(e) => e.stopPropagation()}
    >
      {
        isHover && !isActive ? (
          <Plus size={20} strokeWidth={1.5} onClick={handleToggleActive} className='h-full bg-[#883734] p-0.5 text-white rounded-full' />
        ) : !isHover && isActive ? (
          <X size={20} strokeWidth={1.5} onClick={handleToggleActive} className='h-full bg-[#883734] p-0.5 text-white rounded-full' />
        ) : isHover && isActive ? (
          <X size={20} strokeWidth={1.5} onClick={handleToggleActive} className='h-full bg-[#883734] p-0.5 text-white rounded-full' />
        ) : null
      }
      {VisibleTags.map((label, index) => (
        <span
          key={index}
          className="px-1.5 py-0.75 text-caption2 rounded-[4px] h-full border"
          style={labelStyle(label, adminTags)}
        >
          {label}
        </span>
      ))}
      {HiddenTags.length > 0 && (
        <div
          className="relative inline-block"
          onMouseEnter={() => setIsHoverChip(true)}
          onMouseLeave={() => setIsHoverChip(false)}
        >
          <span
            className={`px-1.5 py-0.75 text-caption2 rounded-[4px] h-full text-white ${newUpdate ? "bg-black" : "bg-[#AAAAAA]"
              }`}
          >
            +{HiddenTags.length}
          </span>

          {isHoverChip && (
            <div 
              className="absolute left-0 top-full mt-1 z-50 cursor-default"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreLabels orderId={orderId} HiddenTags={HiddenTags} adminTags={adminTags} />
            </div>
          )}
        </div>
      )}

      {
        isActive && (
          <div 
            className={`absolute left-0 z-50 cursor-default ${positionAbove ? 'bottom-full mb-1' : 'top-full mt-1'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <AddLabels
              orderId={orderId}
              orderRef={orderRef}
              customerId={customerId}
              labels={labels}
              adminTags={adminTags}
              fetchTags={fetchTags}
              onUpdateOrder={onUpdateOrder}
            />
          </div>
        )
      }
    </div>

  )
}

export default TagsCell